/**
 * The selected screen's own fields.
 *
 * The `layout` subtlety (follow-up 15 / T09's report): `Screen.layout` is optional in lib with a
 * Zod `.default('single')`, so an older row can arrive without it. The form DISPLAYS an absent
 * `layout` as `single` but writes nothing until the author picks a value — defaulting it on load
 * would be a transform on the working document, which B9 forbids.
 */
import { computed } from 'vue'

import type { ComputedRef } from 'vue'
import type { NativeSelectOption } from '@shared/ui/fields'
import type { Screen } from './interfaces'
import type { ScreenLayout } from './types'
import type { ContentBlockOwner } from './store/interfaces'

import { useAssessmentEditorStore } from './store'

const LAYOUT_LABELS: Record<ScreenLayout, string> = {
  'single': 'Single column',
  'two-column': 'Two columns',
}

interface ScreenEditorReturn {
  refScreen: ComputedRef<Screen | null>
  refHasScreens: ComputedRef<boolean>
  refOwner: ComputedRef<ContentBlockOwner | null>
  refLayout: ComputedRef<ScreenLayout>
  refLayoutOptions: ComputedRef<NativeSelectOption[]>
  refCta: ComputedRef<string>
  refIsSubmitScreen: ComputedRef<boolean>
  setLayout: (value: ScreenLayout) => void
  setCta: (value: string) => void
  toggleSubmitScreen: () => void
}

export const useScreenEditor = (): ScreenEditorReturn => {
  const store = useAssessmentEditorStore()

  const refScreen = computed<Screen | null>(() => store.getSelectedScreen)

  const refHasScreens = computed<boolean>(() => store.hasScreens)

  const refOwner = computed<ContentBlockOwner | null>(() =>
    refScreen.value ? { screenId: refScreen.value.id } : null,
  )

  /** Display-only default. Nothing is written back until `setLayout` is called. */
  const refLayout = computed<ScreenLayout>(() => refScreen.value?.layout ?? 'single')

  const refLayoutOptions = computed<NativeSelectOption[]>(() =>
    (Object.keys(LAYOUT_LABELS) as ScreenLayout[]).map(value => ({
      id: value,
      value,
      text: LAYOUT_LABELS[value],
      selected: value === refLayout.value,
    })),
  )

  const refCta = computed<string>(() => refScreen.value?.cta ?? '')

  /** The schema's `isLast` — the screen whose CTA submits the attempt. */
  const refIsSubmitScreen = computed<boolean>(() => refScreen.value?.isLast === true)

  const setLayout = (value: ScreenLayout): void => {
    if (!refScreen.value) {
      return
    }
    store.updateScreen(refScreen.value.id, { layout: value })
  }

  /** An emptied CTA deletes the key rather than storing `''`, which would override the player's own
   *  wording with nothing. */
  const setCta = (value: string): void => {
    if (!refScreen.value) {
      return
    }
    store.updateScreen(refScreen.value.id, { cta: value.trim() ? value : null })
  }

  const toggleSubmitScreen = (): void => {
    if (!refScreen.value) {
      return
    }
    store.updateScreen(refScreen.value.id, { isLast: !refIsSubmitScreen.value })
  }

  return {
    refScreen,
    refHasScreens,
    refOwner,
    refLayout,
    refLayoutOptions,
    refCta,
    refIsSubmitScreen,
    setLayout,
    setCta,
    toggleSubmitScreen,
  }
}
